// Postback (postback.sh) — Mobile Measurement Partner integration.
//
// Scope
// -----
// Native iOS ONLY (web + Android early-return). Postback is the MMP
// that joins Apple Search Ads spend → install → RevenueCat subscription
// revenue at keyword granularity — a signal RC's built-in ASA
// integration can't produce alone. Web pages already have GTM/GA4 for
// their equivalent web funnel, so we don't dual-track from JS.
//
// Architecture
// ------------
// The Postback iOS SDK is a native Swift XCFramework. We surface it to
// our React layer via a THIN Capacitor plugin registered natively in
// `ios/App/App/PostbackPlugin.swift` (see /app/POSTBACK_SETUP.md).
// This file is the JS bridge; the native side does the real work.
//
// If the native plugin isn't present (dev builds, older App Store
// versions before we shipped it) every call becomes a no-op — the
// paywall / checkout / attribution flow is NEVER blocked on this.

import { registerPlugin, Capacitor } from "@capacitor/core";
import { isNativeApp, nativePlatform } from "@/lib/native";

// `registerPlugin` returns a proxy — if the native side never
// registered a plugin named "Postback", the proxy still resolves but
// calls throw at invocation time. We guard every call in a try/catch
// AND with an availability check so a missing plugin degrades to a
// no-op instead of crashing the paywall.
const PostbackNative = registerPlugin("Postback");

let _configured = false;

/** iOS OR Android — Postback supports both via the same JS bridge.
 *  Calofay's Android paywall goes through Google Play (RC-managed),
 *  and while RC alone handles Google Play subscription revenue,
 *  Postback adds Google Ads / TikTok Ads attribution + funnel data
 *  that RC's built-in integration can't produce. Enabled on both
 *  native platforms; web is always false so the paywall / checkout
 *  flow on the browser is unaffected. */
export function isPostbackEnabled() {
  if (!isNativeApp()) return false;
  const p = nativePlatform();
  if (p !== "ios" && p !== "android") return false;
  try {
    // `isPluginAvailable` returns false when the native class wasn't
    // registered in `Capacitor.plugins`, which is exactly our
    // "not shipped yet" case — return false so nothing throws.
    return Capacitor.isPluginAvailable && Capacitor.isPluginAvailable("Postback");
  } catch {
    return false;
  }
}

/**
 * Kick off the Postback SDK. Idempotent — safe to call from multiple
 * mount points. The native plugin internally calls
 * `Postback.shared.configure(...)` with `enableAppleAdsAttribution=true`
 * so the AdServices token is captured on the FIRST install launch
 * (there is no second chance — the token TTL is 24 h).
 *
 * We forward `customerUserId` so Postback can correlate the install
 * to our Calofay `users._id` from day 1, even before the first purchase.
 */
export async function configurePostback(userId) {
  if (!isPostbackEnabled()) return;
  if (_configured) {
    // Just update the customer user id if it changed (user switched
    // accounts, or the id wasn't known on first configure).
    if (userId) {
      try { await PostbackNative.setCustomerUserId({ customerUserId: String(userId) }); }
      catch (_) {}
    }
    return;
  }
  try {
    await PostbackNative.configure({
      customerUserId: userId ? String(userId) : null,
    });
    _configured = true;
  } catch (e) {
    console.warn("[Postback] configure failed", e);
  }
}

/** Returns the install-scoped Postback id, or `null` if the SDK
 *  hasn't finished install registration yet. Used to plumb the id
 *  into RevenueCat as a subscriber attribute so RC webhook events
 *  can be joined to the original Postback install by their backend. */
export async function getPostbackId() {
  if (!isPostbackEnabled()) return null;
  try {
    const res = await PostbackNative.getPostbackId();
    return res?.postbackId || null;
  } catch (e) {
    console.warn("[Postback] getPostbackId failed", e);
    return null;
  }
}

/**
 * Fire a standard Postback event. `name` must be one of the SDK's
 * enum values (session_start, login, sign_up, register, purchase,
 * subscribe, start_trial, add_payment_info, initiate_checkout,
 * view_content, view_item, search, share, tutorial_complete,
 * achieve_level, level_start, level_complete). Anything else should
 * be sent through `sendCustomEvent` below with an explicit name.
 *
 * `params` may include `revenue` (Number) and `currency` (3-letter
 * ISO code, e.g. "USD"/"SAR") for monetary events — the SDK is strict
 * about the 3-letter shape and silently drops the field otherwise.
 */
export async function sendEvent(name, params = {}) {
  if (!isPostbackEnabled()) return;
  try {
    await PostbackNative.sendEvent({ name, params });
  } catch (e) {
    console.warn("[Postback] sendEvent failed", name, e);
  }
}

/** Custom (out-of-taxonomy) events — Postback treats these separately
 *  and requires an explicit non-empty `name`. Trim + skip on empty so
 *  we don't waste an event on a bad caller. */
export async function sendCustomEvent(name, params = {}) {
  if (!isPostbackEnabled()) return;
  const clean = typeof name === "string" ? name.trim() : "";
  if (!clean) return;
  try {
    await PostbackNative.sendCustomEvent({ name: clean, params });
  } catch (e) {
    console.warn("[Postback] sendCustomEvent failed", name, e);
  }
}

/** Trigger Postback's built-in verify-connectivity ping. Postback's
 *  setup wizard listens for this specific event to flip "Waiting for
 *  first event" into the live dashboard. Returns
 *  `{ success: boolean, message: string }`. On non-native builds,
 *  returns `{ success: false, message: "not native" }` so callers
 *  can still render a result. Use from an Admin/Debug button — never
 *  fire on normal user flows (it counts against your event quota). */
export async function sendTestEvent() {
  if (!isPostbackEnabled()) {
    return { success: false, message: "Postback not available (web / Android / plugin missing)" };
  }
  try {
    const res = await PostbackNative.sendTestEvent();
    return {
      success: !!res?.success,
      message: res?.message || "",
    };
  } catch (e) {
    console.warn("[Postback] sendTestEvent failed", e);
    return { success: false, message: e?.message || "sendTestEvent threw" };
  }
}
